import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useToast } from '../../hooks/useToast';
import { format } from "date-fns";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '../common/Card';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Modal } from '../common/Modal';
import { DatePickerCalendar } from '../common/DatePickerCalendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../common/Popover';
import ConfirmModalAlert from '../common/ConfirmModalAlert';
import {
  CalendarIcon,
  ClockIcon,
  PlayIcon,
  Pencil1Icon,
  TrashIcon,
  PlusIcon,
} from '@radix-ui/react-icons';
import { updateLog, deleteLog, createLog } from '../../api/activity-log';
import { cn } from '../../utils/cn';
import { convertToTimezone } from '../../utils/timezone';

const DatePicker = ({ date, setDate, label }) => {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            className={cn(
              "w-full justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? format(date, "PPP") : <span>Pick a date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <DatePickerCalendar
            mode="single"
            selected={date}
            onSelect={setDate}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

const TimePicker = ({ value, onChange, label }) => {
  // Parse the 24h time to get hours and period
  const get12HourTime = (time24h) => {
    if (!time24h) return { time: '', period: 'AM' };
    const [hours24, minutes] = time24h.split(':');
    const hours24Num = parseInt(hours24, 10);
    const period = hours24Num >= 12 ? 'PM' : 'AM';
    const hours12 = hours24Num % 12 || 12;
    return {
      time: `${hours12.toString().padStart(2, '0')}:${minutes}`,
      period
    };
  };

  // Convert 12h time and period back to 24h
  const get24HourTime = (time12h, period) => {
    if (!time12h) return '';
    const [hours12, minutes] = time12h.split(':');
    let hours24 = parseInt(hours12, 10);
    
    if (hours24 === 12) {
      hours24 = period === 'AM' ? 0 : 12;
    } else if (period === 'PM') {
      hours24 += 12;
    }
    
    return `${hours24.toString().padStart(2, '0')}:${minutes}`;
  };

  const { time: time12h, period } = get12HourTime(value);

  const handleTimeChange = (newTime12h) => {
    const time24h = get24HourTime(newTime12h, period);
    onChange({ target: { value: time24h } });
  };

  const handlePeriodChange = (newPeriod) => {
    const time24h = get24HourTime(time12h, newPeriod);
    onChange({ target: { value: time24h } });
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>
      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <Input
            type="time"
            value={time12h}
            onChange={(e) => handleTimeChange(e.target.value)}
            required
            className="w-full bg-background pl-10 border-input [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden [&::-webkit-clear-button]:hidden [&::-webkit-datetime-edit-ampm-field]:hidden"
          />
          <ClockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        </div>
        <select
          value={period}
          onChange={(e) => handlePeriodChange(e.target.value)}
          className="w-20 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
};

const ActivityLogsCard = ({ logs = [], activityId, onLogsUpdate }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const { toast } = useToast();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [localLogs, setLocalLogs] = useState(logs);
  const [editForm, setEditForm] = useState({
    fromDate: '',
    fromTime: '',
    toDate: '',
    toTime: '',
  });
  const [addForm, setAddForm] = useState({
    fromDate: new Date(),
    fromTime: format(new Date(), 'HH:mm'),
    toDate: new Date(),
    toTime: format(new Date(), 'HH:mm'),
  });

  useEffect(() => {
    setLocalLogs(logs);
  }, [logs]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const currentYear = new Date().getFullYear();
    const logYear = date.getFullYear();

    const options = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      ...(logYear !== currentYear && { year: 'numeric' }),
    };

    return date.toLocaleDateString('en-US', options);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return `${date.getHours().toString().padStart(2, '0')}:${date
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;
  };

  const formatTimeRange = (log) => {
    const startTime = formatTime(log.started_at);
    const endTime = log.ended_at
      ? formatTime(log.ended_at)
      : formatTime(currentTime);
    return `${startTime} - ${endTime}`;
  };

  const groupLogsByDay = (logs) => {
    const groups = {};
    const sortedLogs = [...logs].sort((a, b) => {
      if (!a.ended_at && b.ended_at) return -1;
      if (a.ended_at && !b.ended_at) return 1;
      return new Date(b.started_at).getTime() - new Date(a.started_at).getTime();
    });

    sortedLogs.forEach(log => {
      const date = convertToTimezone(new Date(log.started_at).toDateString());
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(log);
    });

    return Object.entries(groups);
  };

  const handleEdit = (log) => {
    const fromDate = new Date(log.started_at);
    const toDate = log.ended_at ? new Date(log.ended_at) : new Date();

    setEditForm({
      fromDate,
      fromTime: format(fromDate, 'HH:mm'),
      toDate,
      toTime: format(toDate, 'HH:mm'),
    });
    setSelectedLog(log);
    setShowEditModal(true);
  };

  const handleDelete = (log) => {
    setSelectedLog(log);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteLog(activityId, selectedLog.id);
      
      // Update local state
      const updatedLogs = localLogs.filter(log => log.id !== selectedLog.id);
      setLocalLogs(updatedLogs);
      if (onLogsUpdate) {
        onLogsUpdate(updatedLogs);
      }

      toast({
        title: "Success",
        description: "Log deleted successfully",
        type: "success"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to delete log',
        type: "error"
      });
    } finally {
      setShowDeleteModal(false);
      setSelectedLog(null);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const fromDateTime = new Date(editForm.fromDate);
      fromDateTime.setHours(parseInt(editForm.fromTime.split(':')[0], 10));
      fromDateTime.setMinutes(parseInt(editForm.fromTime.split(':')[1], 10));

      const toDateTime = new Date(editForm.toDate);
      toDateTime.setHours(parseInt(editForm.toTime.split(':')[0], 10)); 
      toDateTime.setMinutes(parseInt(editForm.toTime.split(':')[1], 10));

      if (fromDateTime >= toDateTime) {
        throw new Error('Start time must be before end time');
      }

      await updateLog(activityId, selectedLog.id, {
        fromDateTime,
        toDateTime
      });

      // Update local state
      const updatedLogs = localLogs.map(log => 
        log.id === selectedLog.id ? { ...log, started_at: fromDateTime, ended_at: toDateTime } : log
      );
      setLocalLogs(updatedLogs);
      if (onLogsUpdate) {
        onLogsUpdate(updatedLogs);
      }

      toast({
        title: "Success",
        description: "Log updated successfully",
        type: "success"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to update log',
        type: "error"
      });
    } finally {
      setShowEditModal(false);
      setSelectedLog(null);
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      const fromDateTime = new Date(addForm.fromDate);
      fromDateTime.setHours(parseInt(addForm.fromTime.split(':')[0], 10));
      fromDateTime.setMinutes(parseInt(addForm.fromTime.split(':')[1], 10));

      const toDateTime = new Date(addForm.toDate);
      toDateTime.setHours(parseInt(addForm.toTime.split(':')[0], 10)); 
      toDateTime.setMinutes(parseInt(addForm.toTime.split(':')[1], 10));

      if (fromDateTime >= toDateTime) {
        throw new Error('Start time must be before end time');
      }

      const newLog = await createLog(activityId, {
        fromDateTime,
        toDateTime
      });

      // Update local state with the new log
      const updatedLogs = [...localLogs, { 
        ...newLog, 
        started_at: fromDateTime,
        ended_at: toDateTime
      }];
      setLocalLogs(updatedLogs);
      
      // Notify parent component
      if (onLogsUpdate) {
        onLogsUpdate(updatedLogs);
      }

      toast({
        title: "Success",
        description: "Log created successfully",
        type: "success"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to create log',
        type: "error"
      });
    } finally {
      setShowAddModal(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xl font-semibold">Recent Logs</CardTitle>
        <Button
          onClick={() => setShowAddModal(true)}
          size="sm"
          className="h-8"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Log
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <AnimatePresence mode="popLayout">
            {groupLogsByDay(localLogs).map(([date, dayLogs], groupIndex) => (
              <motion.div
                key={date}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2, delay: groupIndex * 0.05 }}
                className="space-y-2"
              >
                <div className="sticky top-0 bg-background/95 backdrop-blur-sm py-2">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    {formatDate(new Date(date))}
                  </h3>
                </div>
                <div className="space-y-2">
                  {dayLogs.map((log, index) => (
                    <motion.div
                      key={log.id || index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      className={`flex items-start space-x-4 rounded-lg border p-4 ${
                        !log.ended_at ? 'bg-primary/5 dark:bg-primary/10' : ''
                      }`}
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
                        {!log.ended_at ? (
                          <PlayIcon className="h-4 w-4 text-primary animate-pulse" />
                        ) : (
                          <ClockIcon className="h-4 w-4 text-primary" />
                        )}
                      </div>
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-sm text-muted-foreground">
                            <CalendarIcon className="mr-2 h-3 w-3" />
                            <span>{formatTimeRange(log)}</span>
                            {!log.ended_at && (
                              <span className="ml-2 text-primary">(In Progress)</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(log)}
                              disabled={!log.ended_at}
                            >
                              <Pencil1Icon className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(log)}
                              disabled={!log.ended_at}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {localLogs.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <ClockIcon className="h-8 w-8 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No logs recorded yet</p>
            </div>
          )}
        </div>
      </CardContent>

      {/* Add Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      >
        <form onSubmit={handleAddSubmit}>
          <Modal.Header>
            <Modal.Title>Add New Log</Modal.Title>
            <Modal.Description>
              Create a new log entry with start and end time.
            </Modal.Description>
          </Modal.Header>
          <div className="p-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <DatePicker
                  date={addForm.fromDate}
                  setDate={(date) => setAddForm({ ...addForm, fromDate: date })}
                  label="Start Date"
                />
                <TimePicker
                  value={addForm.fromTime}
                  onChange={(e) => setAddForm({ ...addForm, fromTime: e.target.value })}
                  label="Start Time"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <DatePicker
                  date={addForm.toDate}
                  setDate={(date) => setAddForm({ ...addForm, toDate: date })}
                  label="End Date"
                />
                <TimePicker
                  value={addForm.toTime}
                  onChange={(e) => setAddForm({ ...addForm, toTime: e.target.value })}
                  label="End Time"
                />
              </div>
            </div>
          </div>
          <Modal.Footer>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button type="submit">
              Create Log
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
      >
        <form onSubmit={handleEditSubmit}>
          <Modal.Header>
            <Modal.Title>Edit Log</Modal.Title>
            <Modal.Description>
              Update the start and end time for this log.
            </Modal.Description>
          </Modal.Header>
          <div className="p-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <DatePicker
                  date={editForm.fromDate}
                  setDate={(date) => setEditForm({ ...editForm, fromDate: date })}
                  label="Start Date"
                />
                <TimePicker
                  value={editForm.fromTime}
                  onChange={(e) => setEditForm({ ...editForm, fromTime: e.target.value })}
                  label="Start Time"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <DatePicker
                  date={editForm.toDate}
                  setDate={(date) => setEditForm({ ...editForm, toDate: date })}
                  label="End Date"
                />
                <TimePicker
                  value={editForm.toTime}
                  onChange={(e) => setEditForm({ ...editForm, toTime: e.target.value })}
                  label="End Time"
                />
              </div>
            </div>
          </div>
          <Modal.Footer>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button type="submit">
              Save Changes
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModalAlert
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Log"
        message="Are you sure you want to delete this log?"
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
      />
    </Card>
  );
};

export default ActivityLogsCard; 